import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { loginAdminUser } from '../../api/auth.js';
import { useDispatch } from 'react-redux';
import { login } from './reducer/authSlice.js';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../hooks/useNotification.js';
import { getDefaultRoute } from '../../config/rolePermissions.js';

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleRememberMeChange = (event) => {
    setRememberMe(event.target.checked);
  };

  const { showErrorNotification, showSuccessNotification } = useNotification();

  const handleSubmit = async () => {
    try {
      setLoading(true)
      const res = await loginAdminUser(username, password, rememberMe);
      if (res?.success) {
        const role = res.data?.user?.role;
        dispatch(login({ token: res.data?.token, role }));
        navigate(getDefaultRoute(role));
        showSuccessNotification('Login success');
      } else {
        showErrorNotification('Login failed, please check your credentials');
      }
    } catch (error) {
      showErrorNotification('Login failed, please check your credentials');
      console.error('Login failed:', error);
    }
    setLoading(false)
  };

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      height="100vh"
    >
      {/* Left Side - Image and Welcome Text */}
      <Box
        display={{ xs: 'none', md: 'flex' }}
        width="50%"
        sx={{
          backgroundImage: "url('/assets/images/login/welcome-back-admin.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          color: 'white',
        }}
      >
        <Box>
          <Typography
            variant="h2"
            fontWeight="bold"
            sx={{ color: '#fff', margin: '0 auto 50px auto' }}
          >
            Welcome Back, Admin!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#fff',
              fontSize: '20px',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            Manage your dashboard, monitor activity, and stay in control. Sign
            in to access the admin panel.
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Login Form */}
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: { xs: '100%', md: '50%' },
        }}
      >
        {/* Logo */}
        <Box display="flex" justifyContent="start" mb={2}>
          <img src="https://www.favordating.com/assets/images/favor-logo.svg" alt="Logo" width={163} />
        </Box>

        <Typography variant="h4" fontWeight="bold" mb={2}>
          Secure Access to Your Dashboard
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          mb={4}
          sx={{ fontSize: '16px' }}
        >
          Enter your credentials below to continue
        </Typography>

        <Box component="form">
          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={handleUsernameChange}
            placeholder="Enter email"
            variant="outlined"
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              height: '50px',
              '& .MuiInputLabel-root': { color: '#3E3E3E' }, // Default label color
              '& .MuiInputLabel-root.Mui-focused': { color: '#916008' }, // Label color when active
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: '#916008' },
                '&.Mui-focused fieldset': { borderColor: '#916008' },
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            placeholder="Enter password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            variant="outlined"
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              height: '50px',
              '& .MuiInputLabel-root': { color: '#3E3E3E' }, // Default label color
              '& .MuiInputLabel-root.Mui-focused': { color: '#916008' }, // Label color when active
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: '#916008' },
                '&.Mui-focused fieldset': { borderColor: '#916008' },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={handleRememberMeChange}
                sx={{ '&.Mui-checked': { color: '#916008' } }} />
            }
            label="Remember Me"
            sx={{
              '& .MuiFormControlLabel-label': {
                color: '#000',
                '&.Mui-focused': { color: '#916008', margin: '20px 0 30px 0' },
              },
            }}
          />
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 4,
              color: '#fff',
              fontSize: '15px',
              padding: '12px 10px',
              fontWeight: '500',
              background: '#916008',
              textTransform: 'capitalize',
            }}
            onClick={handleSubmit}
          >
            Sign in
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default AdminLogin;
